import type { IconName } from "@/components/ui/icon";
import type { CandidateReviewSummary } from "@/lib/api/model";

export type ReviewDecision = CandidateReviewSummary["decision"];
export type ReviewReason = NonNullable<CandidateReviewSummary["reason"]>;

export const REVIEW_DECISIONS: ReviewDecision[] = ["yes", "maybe", "no"];

export const REVIEW_DECISION_LABELS: Record<ReviewDecision, string> = {
  yes: "Yes",
  maybe: "Maybe",
  no: "Not a match",
};

export const REVIEW_REASON_OPTIONS: { value: ReviewReason; label: string }[] = [
  { value: "already_contacted", label: "Already contacted" },
  { value: "failed_assessment", label: "Failed assessment" },
  { value: "not_a_fit", label: "Not a fit" },
  { value: "other", label: "Other" },
];

export const REVIEW_REASON_LABELS: Record<ReviewReason, string> =
  Object.fromEntries(
    REVIEW_REASON_OPTIONS.map((o) => [o.value, o.label]),
  ) as Record<ReviewReason, string>;

export const REVIEW_DECISION_ICONS: Record<ReviewDecision, IconName> = {
  yes: "IconCircleCheck",
  maybe: "IconQuestionmarkCircle2",
  no: "IconCircleX",
};

/** Text colours shared by the badge, the pressed control button and history cards. */
export const REVIEW_DECISION_TEXT: Record<ReviewDecision, string> = {
  yes: "text-[#5a7a00] dark:text-[#edffb2]",
  maybe: "text-[#8a5a00] dark:text-[#f0d08a]",
  no: "text-[#b3261e] dark:text-[#ffb4ab]",
};

/** Decision-specific badge palette (bg / border / text), light + dark. */
export const REVIEW_DECISION_STYLES: Record<ReviewDecision, string> = {
  yes: `bg-[rgba(90,122,0,0.1)] border-[rgba(90,122,0,0.25)] dark:bg-[rgba(220,243,43,0.1)] ${REVIEW_DECISION_TEXT.yes}`,
  maybe: `bg-[rgba(180,120,20,0.1)] border-[rgba(180,120,20,0.35)] ${REVIEW_DECISION_TEXT.maybe}`,
  no: `bg-[rgba(200,50,50,0.08)] border-[rgba(200,50,50,0.25)] dark:bg-[rgba(255,120,120,0.1)] ${REVIEW_DECISION_TEXT.no}`,
};

/** History card palette: a faint decision tint behind neutral text. */
export const REVIEW_DECISION_CARD_STYLES: Record<ReviewDecision, string> = {
  yes: "bg-[rgba(90,122,0,0.05)] border-[rgba(90,122,0,0.22)] dark:bg-[rgba(220,243,43,0.04)] dark:border-[rgba(220,243,43,0.2)]",
  maybe: "bg-[rgba(180,120,20,0.05)] border-[rgba(180,120,20,0.25)] dark:bg-[rgba(240,208,138,0.04)] dark:border-[rgba(240,208,138,0.2)]",
  no: "bg-[rgba(200,50,50,0.04)] border-[rgba(200,50,50,0.2)] dark:bg-[rgba(255,120,120,0.04)] dark:border-[rgba(255,120,120,0.2)]",
};

/** Pressed-button palette of the review control (stronger fill, no border). */
export const REVIEW_DECISION_ACTIVE_STYLES: Record<ReviewDecision, string> = {
  yes: `bg-[rgba(90,122,0,0.14)] dark:bg-[rgba(220,243,43,0.14)] ${REVIEW_DECISION_TEXT.yes}`,
  maybe: `bg-[rgba(180,120,20,0.16)] ${REVIEW_DECISION_TEXT.maybe}`,
  no: `bg-[rgba(200,50,50,0.12)] dark:bg-[rgba(255,120,120,0.14)] ${REVIEW_DECISION_TEXT.no}`,
};

/** What a decision write sends besides the decision itself. */
export interface ReviewDetails {
  reason?: ReviewReason | null;
  note?: string | null;
}

/**
 * Optimistic stand-in for a review the server has not confirmed yet. Kept
 * structurally close to the server summary so the badge renders unchanged;
 * the settle-time invalidation replaces it with the real row.
 */
export function buildOptimisticReview(args: {
  profileId: string;
  searchId: string;
  projectId: string | null;
  decision: ReviewDecision;
  details?: ReviewDetails;
  reviewer: { id: string | null; name: string | null };
  previous?: CandidateReviewSummary | null;
}): CandidateReviewSummary {
  const { details } = args;
  // Only this search's own review is rewritten in place; a review from
  // another search is a different row.
  const previous = args.previous?.in_this_search ? args.previous : null;
  return {
    id: previous?.id ?? `optimistic-${args.profileId}`,
    profile_id: args.profileId,
    decision: args.decision,
    // A resubmitted `no` without a reason keeps the existing one.
    reason:
      args.decision === "no"
        ? (details?.reason ??
          (previous?.decision === "no" ? previous.reason : null) ??
          null)
        : null,
    // The server keeps an existing note when a resubmitted `no` doesn't
    // supply one.
    note:
      details?.note?.trim() ||
      (args.decision === "no" ? (previous?.note ?? null) : null),
    reviewed_by_id: args.reviewer.id,
    reviewed_by_name: args.reviewer.name,
    reviewed_at: new Date().toISOString(),
    project_id: args.projectId ?? previous?.project_id ?? "",
    project_title: previous?.project_title ?? null,
    search_id: args.searchId,
    search_name: previous?.search_name ?? null,
    in_this_search: true,
  };
}

export const REVIEW_PROFILE_LIMIT = 500;

/** Why a bulk review can't be sent (the API caps one write at 500 ids). */
export function getReviewLimitError(count: number): string | null {
  if (count <= REVIEW_PROFILE_LIMIT) return null;
  const overflow = count - REVIEW_PROFILE_LIMIT;
  return `You can review up to ${REVIEW_PROFILE_LIMIT} profiles at a time. Reduce your selection by ${overflow} and try again.`;
}
